import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { loadStoredPaginatorState, persistPaginatorState, StoredPaginatorState } from './paginator-storage.helper';

export interface PageChangeEvent {
  page: number;
  first: number;
  rows: number;
  pageCount: number;
}

@Component({
  selector: 'app-custom-paginator',
  templateUrl: './custom-paginator.component.html',
  styleUrls: ['./custom-paginator.component.scss'],
  standalone: false,
})
export class CustomPaginatorComponent implements OnInit, OnChanges {

  @Input() pageNumber = 1;
  @Input() rows = 10;
  @Input() totalRecords = 0;
  @Input() rowsPerPageOptions: number[] = [10, 20, 50];
  @Input() pageLinkSize = 5;
  @Input() showCurrentPageReport = false;
  @Input() currentPageReportTemplate = 'Showing {first} to {last} of {totalRecords} items';
  @Input() showPageSizeSelector = true;
  @Input() alwaysShow = true;
  @Input() disabled = false;
  @Input() showFirstLastIcon = true;
  @Input() showPageLinks = true;
  @Input() tableName?: string;

  @Output() onPageChange = new EventEmitter<PageChangeEvent>();

  visiblePageNumbers: number[] = [];

  private lastStoredState: StoredPaginatorState | null = null;

  public currentPageNumber = 1;
  public currentRows = 10;

  ngOnInit(): void {
    this.currentPageNumber = this.normalizePageNumber(this.pageNumber);
    this.currentRows = this.rows ?? 10;

    this.normalizeCurrentPageNumber();
    this.calculateVisiblePageNumbers();

    this.restoreFromStorage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageNumber']) {
      this.currentPageNumber = this.normalizePageNumber(this.pageNumber);
    }

    if (changes['rows']) {
      this.currentRows = this.rows ?? 10;
    }

    // Re-restore when tableName changes
    if (changes['tableName'] && !changes['tableName'].firstChange) {
      this.restoreFromStorage();
      return;
    }

    if (changes['pageNumber'] || changes['rows'] || changes['totalRecords']) {
      this.normalizeCurrentPageNumber();
      this.calculateVisiblePageNumbers();
    }
  }

  get pageCount(): number {
    if (this.currentRows <= 0) {
      return 1;
    }

    const computedPageCount = Math.ceil(this.totalRecords / this.currentRows);
    return Math.max(1, computedPageCount);
  }

  get page(): number{
    return this.currentPageNumber;
  }

  get isFirstPage(): boolean {
    return this.currentPageNumber <= 1;
  }

  get isLastPage(): boolean {
    return this.currentPageNumber >= this.pageCount;
  }

  get shouldRender(): boolean {
    if (this.alwaysShow) {
      return true;
    }

    return this.totalRecords > this.currentRows;
  }

  get reportText(): string {
    const firstItemNumber = this.totalRecords
      ? ((this.currentPageNumber - 1) * this.currentRows) + 1
      : 0;

    const lastItemNumber = Math.min(this.currentPageNumber * this.currentRows, this.totalRecords);

    return this.currentPageReportTemplate
      .replace('{first}', String(firstItemNumber))
      .replace('{last}', String(lastItemNumber))
      .replace('{totalRecords}', String(this.totalRecords));
  }

  // In case multiple paginators in the same view
  get pageSizeSelectId(): string {
    const safeTableName = String(this.tableName ?? 'paginator').replace(/[^\w-]/g, '_');
    return `pageSize-${safeTableName}`;
  }

  goToFirstPage(): void {
    this.tryChangePage(1);
  }

  goToPreviousPage(): void {
    if (this.isFirstPage) {
      return;
    }

    this.tryChangePage(this.currentPageNumber - 1);
  }

  goToNextPage(): void {
    if (this.isLastPage) {
      return;
    }

    this.tryChangePage(this.currentPageNumber + 1);
  }

  goToLastPage(): void {
    this.tryChangePage(this.pageCount);
  }

  onPageNumberClick(pageNumber: number): void {
    this.tryChangePage(pageNumber);
  }

  onRowsChange(newRows: number): void {
    const normalizedRows = Number(newRows);

    if (this.disabled) {
      return;
    }

    if (!Number.isFinite(normalizedRows) || normalizedRows <= 0) {
      return;
    }

    if (normalizedRows === this.currentRows) {
      return;
    }

    this.currentRows = normalizedRows;
    this.currentPageNumber = 1;

    this.persistToStorage();
    this.calculateVisiblePageNumbers();

    this.emitPageChange(this.currentPageNumber);
  }

  private tryChangePage(nextPageNumber: number): void {
    if (this.disabled) {
      return;
    }

    this.changePage(nextPageNumber);
  }

  private changePage(nextPageNumber: number): void {
    const clampedPageNumber = this.clampPageNumber(nextPageNumber);

    if (clampedPageNumber === this.currentPageNumber) {
      return;
    }

    this.currentPageNumber = clampedPageNumber;

    this.persistToStorage();
    this.calculateVisiblePageNumbers();

    this.emitPageChange(clampedPageNumber);
  }

private emitPageChange(pageNumber: number): void {
  this.onPageChange.emit({
    page: pageNumber,
    first: (pageNumber - 1) * this.currentRows,
    rows: this.currentRows,
    pageCount: this.pageCount,
  });
}

  private clampPageNumber(pageNumber: number): number {
    if (pageNumber < 1) {
      return 1;
    }

    // Do not clamp restored pages before the table response arrives
    if (this.totalRecords > 0 && pageNumber > this.pageCount) {
      return this.pageCount;
    }

    return pageNumber;
  }

  private normalizeCurrentPageNumber(): void {
    this.currentPageNumber = this.clampPageNumber(this.normalizePageNumber(this.currentPageNumber));
  }

  private normalizePageNumber(pageNumber: number): number {
    const normalizedPageNumber = Number(pageNumber);

    if (!Number.isFinite(normalizedPageNumber) || normalizedPageNumber < 1) {
      return 1;
    }

    return Math.floor(normalizedPageNumber);
  }

  private calculateVisiblePageNumbers(): void {
    if (!this.showPageLinks) {
      this.visiblePageNumbers = [];
      return;
    }

    if (this.pageCount <= 1) {
      this.visiblePageNumbers = [];
      return;
    }

    const maxVisibleLinks = Math.max(1, this.pageLinkSize);
    const currentDisplayPage = this.currentPageNumber;
    const totalPages = this.pageCount;

    if (totalPages <= maxVisibleLinks) {
      this.visiblePageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
      return;
    }

    const halfWindow = Math.floor(maxVisibleLinks / 2);

    let startPage = currentDisplayPage - halfWindow;
    let endPage = currentDisplayPage + (maxVisibleLinks - halfWindow - 1);

    if (startPage < 1) {
      const missing = 1 - startPage;
      startPage = 1;
      endPage = endPage + missing;
    }

    if (endPage > totalPages) {
      const overflow = endPage - totalPages;
      endPage = totalPages;
      startPage = startPage - overflow;
    }

    startPage = Math.max(1, startPage);

    const pagesToShow: number[] = [];

    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
      pagesToShow.push(pageNumber);
    }

    this.visiblePageNumbers = pagesToShow;
  }

  private persistToStorage(): void {
    if (!this.tableName) {
      return;
    }

    const state = persistPaginatorState({
      tableName: this.tableName,
      pageNumber: this.currentPageNumber,
      pageSize: this.currentRows
    });

    this.lastStoredState = state ?? null;
  }

  private restoreFromStorage(): void {
    if (!this.tableName) {
      console.warn('[CustomPaginator] Missing tableName input. State restore/persist is disabled.');
      return;
    }

    const stored = loadStoredPaginatorState(this.tableName);
    this.lastStoredState = stored;

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    const inputPageSize = this.rows ?? 10;
    const inputPageNumber = this.normalizePageNumber(this.pageNumber);

    const shouldNotifyParent = storedPageSize !== inputPageSize || storedPageNumber !== inputPageNumber;

    this.currentRows = Number(storedPageSize);
    this.currentPageNumber = Number(storedPageNumber);

    this.normalizeCurrentPageNumber();
    this.calculateVisiblePageNumbers();

    if (shouldNotifyParent) {
      Promise.resolve().then(() => {
        this.emitPageChange(this.currentPageNumber);
      });
    }
  }
}
